import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import {
  Home,
  LogOut,
  FileText,
  Users,
  DollarSign,
  TrendingUp,
  Settings,
  Menu,
  X,
  Briefcase,
  CheckCircle,
  XCircle,
  ArrowLeft,
  AlertTriangle,
  Star,
  Clock,
  Shield,
  Scale,
  Building2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Format currency
const formatCurrency = (value) => {
  if (!value && value !== 0) return "$0";
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Realtor Sidebar
function RealtorSidebar({ realtor, onLogout }) {
  const [collapsed, setCollapsed] = useState(false);
  
  const navItems = [
    { icon: Home, label: "Dashboard", path: "/realtor/dashboard" },
    { icon: FileText, label: "Listings", path: "/realtor/listings" },
    { icon: Briefcase, label: "Transactions", path: "/realtor/transactions" },
    { icon: Users, label: "Buyers", path: "/realtor/buyers" },
    { icon: Building2, label: "Sellers", path: "/realtor/sellers" },
    { icon: DollarSign, label: "Commission", path: "/realtor/commission" },
    { icon: TrendingUp, label: "Analytics", path: "/realtor/analytics" },
    { icon: Settings, label: "Settings", path: "/realtor/settings" },
  ];

  return (
    <aside className={`fixed left-0 top-0 h-full bg-black border-r border-white/[0.06] transition-all z-40 ${
      collapsed ? "w-16" : "w-64"
    }`}>
      <div className="p-4 border-b border-white/[0.06] flex items-center justify-between">
        {!collapsed && (
          <Link to="/realtor/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gold/10 border border-gold/30 flex items-center justify-center">
              <Home className="w-4 h-4 text-gold" />
            </div>
            <span className="font-heading font-bold text-white">REALTOR</span>
          </Link>
        )}
        <button onClick={() => setCollapsed(!collapsed)} className="text-white/50 hover:text-white p-1">
          {collapsed ? <Menu className="w-5 h-5" /> : <X className="w-5 h-5" />}
        </button>
      </div>

      <nav className="p-2 space-y-1">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = window.location.pathname.startsWith(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-3 py-2.5 transition-all duration-300 ${
                isActive ? "bg-gold/10 text-gold border-l-2 border-gold" : "text-white/50 hover:text-white hover:bg-[#0A0A0A]/80 backdrop-blur-sm"
              }`}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {!collapsed && <span className="text-sm">{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      {!collapsed && (
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/[0.06]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-[#0A0A0A]/80 backdrop-blur-sm rounded-full flex items-center justify-center">
              <span className="text-gold font-bold">{realtor?.name?.charAt(0) || "R"}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">{realtor?.name}</p>
              <p className="text-white/40 text-xs truncate">{realtor?.brokerage || "Agent"}</p>
            </div>
          </div>
          <button
            onClick={onLogout}
            className="w-full flex items-center justify-center gap-2 text-white/50 hover:text-[#FF3B30] py-2 border border-white/[0.08] hover:border-red-500/50 transition-all duration-300"
          >
            <LogOut className="w-4 h-4" />
            <span className="text-sm">Logout</span>
          </button>
        </div>
      )}
    </aside>
  );
}

// Risk Score Badge
function RiskScoreBadge({ score }) {
  let color = "bg-red-500/20 text-[#FF3B30] border-red-500/30";
  let label = "High Risk";
  
  if (score >= 80) {
    color = "bg-emerald-500/20 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] border-emerald-500/30";
    label = "Low Risk";
  } else if (score >= 60) {
    color = "bg-amber-500/20 text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)] border-amber-500/30";
    label = "Medium Risk";
  }
  
  return (
    <span className={`px-2 py-1 text-xs font-medium border ${color}`}>
      {score} - {label}
    </span>
  );
}

// Financing Type Badge
function FinancingBadge({ type }) {
  const colors = {
    cash: "bg-emerald-500/20 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] border-emerald-500/30",
    conventional: "bg-blue-500/20 text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)] border-blue-500/30",
    fha: "bg-purple-500/20 text-[#BF5AF2] drop-shadow-[0_0_8px_rgba(191,90,242,0.4)] border-purple-500/30",
    dscr: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
    hard_money: "bg-amber-500/20 text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)] border-amber-500/30"
  };
  
  return (
    <span className={`px-2 py-1 text-xs font-medium border uppercase ${colors[type] || "bg-white/[0.08] text-white/70 border-white/[0.1]"}`}>
      {type.replace(/_/g, " ")}
    </span>
  );
}

export default function RealtorOffersComparePage() {
  const navigate = useNavigate();
  const { listingId } = useParams();
  const [realtor, setRealtor] = useState(null);
  const [listing, setListing] = useState(null);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOffers, setSelectedOffers] = useState([]);

  useEffect(() => {
    fetchData();
  }, [listingId]);

  const fetchData = async () => {
    try {
      const [profileRes, listingsRes, offersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/realtor/auth/me`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/realtor/listings`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/realtor/offers`, { withCredentials: true })
      ]);
      
      setRealtor(profileRes.data);
      
      // Find the specific listing
      const currentListing = listingsRes.data.find(l => l.listing_id === listingId);
      setListing(currentListing);
      
      // Filter offers for this listing
      const listingOffers = offersRes.data.filter(o => o.listing_id === listingId);
      setOffers(listingOffers);
      
      // Auto-select all pending offers for comparison
      const pendingOffers = listingOffers.filter(o => o.status === "pending").map(o => o.offer_id);
      setSelectedOffers(pendingOffers);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/realtor");
      } else {
        toast.error("Failed to load offers");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/realtor/auth/logout`, {}, { withCredentials: true });
      navigate("/realtor");
    } catch (error) {
      navigate("/realtor");
    }
  };

  const handleOfferAction = async (offerId, action, counterAmount = null) => {
    try {
      const payload = { status: action };
      if (counterAmount) {
        payload.counter_amount = counterAmount;
      }
      
      await axios.put(
        `${BACKEND_URL}/api/realtor/offers/${offerId}`,
        payload,
        { withCredentials: true }
      );
      
      toast.success(`Offer ${action}`);
      fetchData();
    } catch (error) {
      toast.error(`Failed to ${action} offer`);
    }
  };

  const toggleOfferSelection = (offerId) => {
    setSelectedOffers(prev => 
      prev.includes(offerId) 
        ? prev.filter(id => id !== offerId)
        : [...prev, offerId]
    );
  };

  const comparisonOffers = offers.filter(o => selectedOffers.includes(o.offer_id));
  
  // Find best offer (highest amount with lowest risk)
  const bestOffer = comparisonOffers.reduce((best, current) => {
    if (!best) return current;
    const currentScore = current.offer_amount * (current.risk_score / 100);
    const bestScore = best.offer_amount * (best.risk_score / 100);
    return currentScore > bestScore ? current : best;
  }, null);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="realtor-offers-compare-page">
      <RealtorSidebar realtor={realtor} onLogout={handleLogout} />

      <main className="ml-64 p-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Link to="/realtor/listings" className="text-white/50 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-1">
              <Scale className="w-6 h-6 text-gold" />
              <h1 className="font-heading text-2xl font-bold text-white uppercase">Compare Offers</h1>
            </div>
            {listing && (
              <p className="text-white/50">{listing.property_address} • {formatCurrency(listing.asking_price)} asking</p>
            )}
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4">
            <p className="text-white/50 text-sm">Total Offers</p>
            <p className="text-2xl font-bold text-white">{offers.length}</p>
          </div>
          <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4">
            <p className="text-white/50 text-sm">Pending</p>
            <p className="text-2xl font-bold text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]">{offers.filter(o => o.status === "pending").length}</p>
          </div>
          <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4">
            <p className="text-white/50 text-sm">Highest Offer</p>
            <p className="text-2xl font-bold text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]">
              {formatCurrency(Math.max(...offers.map(o => o.offer_amount), 0))}
            </p>
          </div>
          <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4">
            <p className="text-white/50 text-sm">Avg Risk Score</p>
            <p className="text-2xl font-bold text-white">
              {offers.length ? Math.round(offers.reduce((sum, o) => sum + o.risk_score, 0) / offers.length) : 0}
            </p>
          </div>
        </div>

        {offers.length === 0 ? (
          <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-12 text-center">
            <FileText className="w-12 h-12 text-white/30 mx-auto mb-3" />
            <p className="text-white/50">No offers on this listing yet</p>
          </div>
        ) : (
          <>
            {/* Offer Selection */}
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4 mb-6">
              <p className="text-white/50 text-sm mb-3">Select offers to compare ({selectedOffers.length} selected)</p>
              <div className="flex flex-wrap gap-2">
                {offers.map((offer) => (
                  <button
                    key={offer.offer_id}
                    onClick={() => toggleOfferSelection(offer.offer_id)}
                    className={`px-4 py-2 border text-sm transition-all duration-300 ${
                      selectedOffers.includes(offer.offer_id)
                        ? "bg-gold/20 border-gold text-gold"
                        : "bg-black border-white/[0.08] text-white/50 hover:border-white/[0.15]"
                    }`}
                  >
                    {offer.buyer_name} - {formatCurrency(offer.offer_amount)}
                    {offer.status !== "pending" && (
                      <span className="ml-2 text-xs opacity-60">({offer.status})</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Comparison Table */}
            {comparisonOffers.length > 0 && (
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/[0.08]">
                      <th className="p-4 text-left text-white/50 font-medium">Metric</th>
                      {comparisonOffers.map((offer) => (
                        <th key={offer.offer_id} className="p-4 text-center min-w-[200px]">
                          <div className="flex items-center justify-center gap-2">
                            {bestOffer?.offer_id === offer.offer_id && (
                              <Star className="w-4 h-4 text-gold fill-current" />
                            )}
                            <span className="text-white font-bold">{offer.buyer_name}</span>
                          </div>
                          <span className={`text-xs ${
                            offer.status === "pending" ? "text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]" :
                            offer.status === "accepted" ? "text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" :
                            "text-white/40"
                          }`}>
                            {offer.status}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Offer Amount */}
                    <tr className="border-b border-white/[0.08]">
                      <td className="p-4 text-white/50">
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4" /> Offer Amount
                        </div>
                      </td>
                      {comparisonOffers.map((offer) => {
                        const isHighest = offer.offer_amount === Math.max(...comparisonOffers.map(o => o.offer_amount));
                        return (
                          <td key={offer.offer_id} className="p-4 text-center">
                            <span className={`font-mono font-bold text-lg ${isHighest ? "text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" : "text-white"}`}>
                              {formatCurrency(offer.offer_amount)}
                            </span>
                            {listing && (
                              <p className="text-xs text-white/40">
                                {Math.round((offer.offer_amount / listing.asking_price) * 100)}% of asking
                              </p>
                            )}
                          </td>
                        );
                      })}
                    </tr>

                    {/* Risk Score */}
                    <tr className="border-b border-white/[0.08]">
                      <td className="p-4 text-white/50">
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4" /> Risk Score
                        </div>
                      </td>
                      {comparisonOffers.map((offer) => (
                        <td key={offer.offer_id} className="p-4 text-center">
                          <RiskScoreBadge score={offer.risk_score} />
                        </td>
                      ))}
                    </tr>

                    {/* Financing */}
                    <tr className="border-b border-white/[0.08]">
                      <td className="p-4 text-white/50">
                        <div className="flex items-center gap-2">
                          <Briefcase className="w-4 h-4" /> Financing
                        </div>
                      </td>
                      {comparisonOffers.map((offer) => (
                        <td key={offer.offer_id} className="p-4 text-center">
                          <FinancingBadge type={offer.financing_type} />
                        </td>
                      ))}
                    </tr>

                    {/* Down Payment */}
                    <tr className="border-b border-white/[0.08]">
                      <td className="p-4 text-white/50">Down Payment</td>
                      {comparisonOffers.map((offer) => (
                        <td key={offer.offer_id} className="p-4 text-center text-white">
                          {offer.down_payment_percent}%
                          <p className="text-xs text-white/40">
                            {formatCurrency(offer.offer_amount * (offer.down_payment_percent / 100))}
                          </p>
                        </td>
                      ))}
                    </tr>

                    {/* Earnest Money */}
                    <tr className="border-b border-white/[0.08]">
                      <td className="p-4 text-white/50">Earnest Money</td>
                      {comparisonOffers.map((offer) => (
                        <td key={offer.offer_id} className="p-4 text-center text-white">
                          {formatCurrency(offer.earnest_money)}
                        </td>
                      ))}
                    </tr>

                    {/* Closing Timeline */}
                    <tr className="border-b border-white/[0.08]">
                      <td className="p-4 text-white/50">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4" /> Closing Days
                        </div>
                      </td>
                      {comparisonOffers.map((offer) => {
                        const isFastest = offer.closing_timeline_days === Math.min(...comparisonOffers.map(o => o.closing_timeline_days));
                        return (
                          <td key={offer.offer_id} className="p-4 text-center">
                            <span className={isFastest ? "text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] font-bold" : "text-white"}>
                              {offer.closing_timeline_days} days
                            </span>
                          </td>
                        );
                      })}
                    </tr>

                    {/* Contingencies */}
                    <tr className="border-b border-white/[0.08]">
                      <td className="p-4 text-white/50">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" /> Contingencies
                        </div>
                      </td>
                      {comparisonOffers.map((offer) => (
                        <td key={offer.offer_id} className="p-4 text-center">
                          {offer.contingencies?.length > 0 ? (
                            <div className="space-y-1">
                              {offer.contingencies.map((c, idx) => (
                                <span key={idx} className="block text-xs text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)] capitalize">{c}</span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] text-sm">None</span>
                          )}
                        </td>
                      ))}
                    </tr>

                    {/* Actions */}
                    <tr>
                      <td className="p-4 text-white/50">Actions</td>
                      {comparisonOffers.map((offer) => (
                        <td key={offer.offer_id} className="p-4 text-center">
                          {offer.status === "pending" ? (
                            <div className="flex flex-col gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleOfferAction(offer.offer_id, "accepted")}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-none w-full"
                              >
                                <CheckCircle className="w-3 h-3 mr-1" /> Accept
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleOfferAction(offer.offer_id, "rejected")}
                                className="border-red-500 text-red-500 hover:bg-red-500/10 rounded-none w-full"
                              >
                                <XCircle className="w-3 h-3 mr-1" /> Reject
                              </Button>
                            </div>
                          ) : (
                            <span className={`text-sm ${
                              offer.status === "accepted" ? "text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" : "text-white/40"
                            }`}>
                              {offer.status}
                            </span>
                          )}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
